import * as React from "react";
import gsap from "gsap";
import { prefersReducedMotion } from "./prefersReducedMotion";

type BubbleProps = {
  extraClassNames?: string;
}

class Bubbles extends React.Component<BubbleProps, never> {
  private containerRef: any;
  private gsapContext: any;

  constructor(props: BubbleProps) {
    super(props);
    this.containerRef = React.createRef();
  }

  componentDidMount() {
    const container = this.containerRef as HTMLElement;

    if (!container) {
      return;
    }

    if(prefersReducedMotion()) {
      return;
    }

    this.gsapContext = gsap.context(() => {
      const bubbleDelay = 1.5;
      const pathBackdrop = '.wave__path--backdrop';
      const pathFrom = '.wave__path--from';
      const pathTo = container.querySelector('.wave__path--to').getAttribute('d');
      const groupRear = '.wave__group--rear';
      const groupFront = '.wave__group--front';
      const wavesRear = '.wave__group--rear .wave__path';
      
      const tl = gsap.timeline();

      gsap.set([pathFrom, wavesRear, pathBackdrop], {
        visibility: 'visible',
      });

      gsap.set([pathFrom, pathBackdrop], {
        transformOrigin: 'center bottom',
      });

      gsap.set(pathBackdrop, {
        xPercent: -25,
      });

      gsap.set(groupRear, {
        transformOrigin: 'center',
        scaleY: 0.5,
      });

      gsap.set(groupFront, {
        transformOrigin: 'center right',
        opacity: 0,
        scaleX: 1.5,
        scaleY: 2,
      });

      gsap.set(wavesRear, {
        xPercent: '-10',
      });

      Array.from(container.querySelectorAll(wavesRear)).forEach((wave, index) => {
        const tlWave = gsap.timeline();
        
        gsap.set(wave, {
          opacity:  (0.25 * (index + 1)),
        });

        tlWave.to(wave, {
          xPercent: '+=50',
          duration: (3 + (index * 3)),
          delay: ((index * 2.5) * -1),
          ease: 'none',
        });
      });
      
      tl.to(groupRear, {
        opacity: 0,
        scaleY: 0,
        duration: 3.5,
        ease: 'none',
      }, 0);

      tl.to(groupFront, {
        opacity: 1,
        duration: 1.5,
        scaleX: 1,
        scaleY: 1,
      }, 0.5);

      tl.to(pathFrom, {
        attr: { d: pathTo },
        duration: 5,
        ease: 'elastic.out',
      }, 1.5);

      tl.to(pathBackdrop, {
        scaleY: 0,
        scaleX: 1,
        xPercent: 0,
        duration: 3,
        ease: 'none',
      }, 0);

      setTimeout(() => {
        doBubbles(container);
      }, bubbleDelay * 1000);
    }, container);
  }

  componentWillUnmount(): void { 
    this.gsapContext?.revert(); 
  }

  render(): React.ReactNode {
    const {
      extraClassNames,
    } = this.props;

    return (
      <div className={`bubbles ${extraClassNames ? extraClassNames : ''}`} ref={ref => this.containerRef = ref}>
        <svg className="bubbles__wave wave" width="1920" height="400" viewBox="0 0 1920 400" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
          <path className="wave__path wave__path--backdrop" d="M2730 357.104V400H-810V360.72C-561.73 399.986 396.54 375.121 467.4 356.818C521.06 342.958 560.15 332.862 639.64 357.226C705.19 377.247 746.44 358.418 788.3 339.312C846.56 312.718 906 285.585 1033.78 361.928C1054.8 374.5 1076.01 381.858 1101.37 381.873C1108.55 381.869 1116.21 381.278 1124.14 380.052C1144.19 376.731 1166.58 370.968 1187.17 365.669C1201.37 362.012 1214.72 358.576 1225.85 356.316C1259.64 349.464 1279.51 355.555 1303.21 362.818C1337.47 373.319 1379.72 386.271 1483.59 366.098C1594.92 344.516 1764.58 350.222 1907.72 355.036C1929.89 355.781 2709.42 356.506 2730 357.104Z" />
          <g className="wave__group wave__group--front">
            <path className="wave__path wave__path--from" d="M2857 347V400H-1211V347.992C-962.25 347.992 324.85 329 408 329C462.1 329 512.36 350.5 589.5 350.5C658.36 350.5 699.2 336.5 744 336.5C811.95 336.5 817.971 349 962 349C987.221 349 1007.75 350.25 1034.5 350.25C1043.25 350.25 1064.5 350.25 1078 350.25C1097.86 350.25 1107.41 350.25 1129 350.25C1145.5 350.25 1163.66 349 1176.5 349C1215 349 1251 349 1276.5 349C1312.29 349 1336.16 353.5 1441 353.5C1557.41 353.5 1654.78 341 1798 341C1820.28 341 2837 347 2857 347Z" />
            <path className="wave__path wave__path--to" d="M2730 343.204V400H-810V347.992C-561.73 399.981 396.54 367.059 467.4 342.826C521.06 324.476 560.15 311.107 639.64 343.366C705.19 369.875 746.44 344.945 788.3 319.647C846.56 284.436 906 248.512 1033.78 349.591C1054.8 366.237 1076.01 375.979 1101.37 376C1108.55 375.994 1116.21 375.211 1124.14 373.588C1144.19 369.191 1166.58 361.561 1187.17 354.544C1201.37 349.703 1214.72 345.154 1225.85 342.161C1259.64 333.089 1279.51 341.153 1303.21 350.77C1337.47 364.674 1379.72 381.823 1483.59 355.114C1594.92 326.538 1764.58 334.092 1907.72 340.466C1929.89 341.453 2709.42 342.412 2730 343.204Z" />
          </g>
          <g className="wave__group wave__group--rear">
            <path className="wave__path" d="M-1446.8 250.4C-1062.8 250.4 -704.4 20 -320.4 20C63.6 20 422 250.4 806 250.4C1190 250.4 1548.4 20 1932.4 20C2316.4 20 2674.8 250.4 3058.8 250.4V813.6H-1446.8V250.4Z" />
            <path className="wave__path" d="M-1446.8 288.8C-1062.8 288.8 -704.4 58.3999 -320.4 58.3999C63.5999 58.3999 422 288.8 806 288.8C1190 288.8 1548.4 58.3999 1932.4 58.3999C2316.4 58.3999 2674.8 288.8 3058.8 288.8V852H-1446.8V288.8Z" />
            <path className="wave__path" d="M-1446.8 314.4C-1062.8 314.4 -704.4 84 -320.4 84C63.5999 84 422 314.4 806 314.4C1190 314.4 1548.4 84 1932.4 84C2316.4 84 2674.8 314.4 3058.8 314.4V877.6H-1446.8V314.4Z" />
            <path className="wave__path" d="M-1446.8 340C-1062.8 340 -704.4 109.6 -320.4 109.6C63.5999 109.6 422 340 806 340C1190 340 1548.4 109.6 1932.4 109.6C2316.4 109.6 2674.8 340 3058.8 340V903.2H-1446.8V340Z" />
          </g>
        </svg>
      </div>
    )
  }
}

export default Bubbles;

export function doBubbles (container: HTMLElement) {
  const minBubbleCount = 20;
  const maxBubbleCount = 40;
  const minBubbleSize = 3;
  const maxBubbleSize = 12;

  setTimeout(() => {
    container.classList.add('bubbles--bubbled'); // cancel the css bubble-wobble animation
  }, 10000);

  const bubbleCount = minBubbleCount + Math.floor(Math.random() * (maxBubbleCount + 1));

  for(let i = 0; i <bubbleCount; i++) {
    const bubble = document.createElement('div');
    bubble.classList.add('bubbles__bubble');
    bubble.classList.add('bubble');

    const bubbleInner = document.createElement('div');
    bubbleInner.classList.add('bubble__inner');

    bubble.appendChild(bubbleInner);
    container.appendChild(bubble);
  }

  const bubbles = Array.from(document.querySelectorAll('.bubble'));

  bubbles.forEach((bubble: HTMLElement) => {
    const bubbleOffset = Math.floor(Math.random() * 101);
    const bubbleSize = minBubbleSize + Math.floor(Math.random() * (maxBubbleSize + 1));
    const bubbleDelay = Math.floor(Math.random() * 3);
    const bubbleSpeed = 3 + Math.floor(Math.random() * 9);
    const bubbleBlur =  Math.floor(Math.random() * 3);
    
    // Apply the new styles
    bubble.style.setProperty('--bubble-offset', `${bubbleOffset}%` );
    bubble.style.setProperty('--bubble-size', `${bubbleSize}px` );
    bubble.style.setProperty('--bubble-delay', `${bubbleDelay}s` );
    bubble.style.setProperty('--bubble-speed', `${bubbleSpeed}s` );
    bubble.style.setProperty('--bubble-blur', `${bubbleBlur}px` );
  });
}